import {DOCUMENT, Inject, Injectable} from '@angular/core';
import {ActivatedRoute, NavigationStart, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {
    AuthMultisiteIsolationService,
    AuthService,
    AuthStorageService,
    AuthToken,
    BaseSiteService,
    EventService,
    LanguageService,
    OAuthLibWrapperService,
    RoutingService,
    StateWithClientAuth,
    UserIdService
} from '@spartacus/core';
import {filter, map, take, tap, withLatestFrom} from 'rxjs/operators';
import {AimoBaseSite} from '../../model/misc.model';
import {BehaviorSubject, combineLatest, firstValueFrom, Observable} from "rxjs";
import {LoginFail} from "./login-logout-fail.action";
import {AimoUserService} from "../user/aimo-user.service";
import {AimoAuthRedirectService} from "./aimo-auth-redirect.service";
import {AimoUser} from "../../model/user.model";
import {openCloseSpinner} from "../../cms-components/shared/utils/spinner/aimo-spinner-utils";
import {AimoCdcTokenAdapter} from './aimo-cdc-token.adapter';
import {AimoTokenStorageService} from './aimo-token-storage.service';
import {AimoAuthRedirectStorageService} from "./aimo-auth-redirect-storage.service";

const LANGUAGE_PLACEHOLDER = 'language';
const CDC_USER_TT = '---CDC_TT---';
const CDC_USER_PT = '---CDC_PT---';
export const AUTH_SESSION_KEY = 'spartacus⚿⚿auth';
export const INTERNAL_AUTH_URL_PARAM = 'crossSiteAuth';
export const EXTERNAL_AUTH_URL_PARAM = 'sso';

@Injectable({
    providedIn: 'root',
})
export class AimoAuthService extends AuthService {
    private cdcLogoutUrl: string;
    private static loginInProgress$ = new BehaviorSubject(false);

    constructor(
        protected store: Store<StateWithClientAuth>,
        protected userIdService: UserIdService,
        protected userService: AimoUserService,
        protected oAuthLibWrapperService: OAuthLibWrapperService,
        protected authStorageService: AuthStorageService,
        protected authRedirectService: AimoAuthRedirectService,
        protected routingService: RoutingService,
        protected router: Router,
        protected activatedRoute: ActivatedRoute,
        protected baseSiteService: BaseSiteService,
        protected languageService: LanguageService,
        protected eventService: EventService,
        protected aimoCdcTokenAdapter: AimoCdcTokenAdapter,
        protected aimoTokenStorageService: AimoTokenStorageService,
        @Inject(DOCUMENT) protected document: Document,
        protected authRedirectStorageService: AimoAuthRedirectStorageService,
        protected authMultisiteIsolationService?: AuthMultisiteIsolationService,
    ) {
        super(store, userIdService, oAuthLibWrapperService, authStorageService, authRedirectService, routingService, authMultisiteIsolationService);
        this.handleCdcLoginStart();
        this.handleCdcLogout();
        // this.initScripts();
    }

    private initScripts(): void {
        this.baseSiteService.get()
            .pipe(
                take(1),
                map((site: AimoBaseSite) => site.cdcApikey),
                filter(Boolean)
            )
            .subscribe(cdcApikey => {
                    const node = document.createElement('script');
                    node.src = 'https://cdns.gigya.com/js/gigya.js?apikey=' + cdcApikey;
                    node.type = 'text/javascript';
                    node.async = true;
                    document.getElementsByTagName('head')[0].appendChild(node);
                }
            );
    }

    private handleCdcLoginStart(): void {
        this.handleSiteAuth(INTERNAL_AUTH_URL_PARAM,
            (param) => {
                const tokenString = atob(decodeURIComponent(param));
                const userToken = JSON.parse(tokenString);
                const token = userToken.token as AuthToken;
                this.document.defaultView?.localStorage?.setItem(AUTH_SESSION_KEY, tokenString);
                this.authStorageService.setToken(token);
                this.userIdService.setUserId(userToken.userId);
                this.routingService.go('/', {replaceUrl: true}).then(val => {
                    if (val) {
                        this.document.location.reload();
                    }
                });
            });
        this.handleSiteAuth(EXTERNAL_AUTH_URL_PARAM,
            (param) => {
                this.forwardToCDCLoginPage(true);
            });
    }

    public isLoginInProgress(): Observable<boolean> {
        return AimoAuthService.loginInProgress$.asObservable();
    }

    private handleSiteAuth(paramName: string, action: (param: string) => void): void {
        this.router.events.pipe(
            filter((event: any) => event instanceof NavigationStart),
            filter((event: any) => event.url !== undefined && event.url.includes(paramName)),
            map((event) => event.url),
            tap((url: string) => this.authRedirectStorageService.setRedirectUrl('/' + url.replace(/\/.*?\//, ''))),// clear language param
            map((url: string) => url.substring(url.indexOf(paramName) + paramName.length + 1)),
        ).subscribe(action);
    }

    forwardToCDCLoginPage(silentLogin: boolean): void {
        combineLatest([
                this.isUserLoggedIn(),
                this.baseSiteService.get(),
                this.languageService.getActive(),
                this.authRedirectService.getRedirectUrl()
            ]
        ).pipe(
            take(1),
        ).subscribe(([isLoggedIn, site, lang, redirectUrl]: [boolean, AimoBaseSite, string, string]) => {
            if (isLoggedIn) {
                this.authRedirectService.aimoRedirect();
                // this.routingService.go('/', {replaceUrl: true});// get rid of the sso param
                return;
            }
            if (site.loginUrl) {
                this.document.defaultView.sessionStorage?.removeItem('gigyaCache');
                this.document.location.href = site.loginUrl.replaceAll(LANGUAGE_PLACEHOLDER, lang ?? 'fi') + (silentLogin ? '&prompt=none' : '');
            } else {
                this.routingService.go('/login');
            }
        });
    }

    cdcLogin(code: string): void {
        this.baseSiteService.get()
            .pipe(
                filter(Boolean),
                take(1),
                withLatestFrom(this.languageService.getActive())
            )
            .subscribe(([site, lang]: [AimoBaseSite, string]) => {
                    const uid = site.uid === 'pikatukku' ? CDC_USER_PT : CDC_USER_TT;
                    firstValueFrom(this.aimoCdcTokenAdapter.exchange(code, uid, lang))
                        .then(
                            token => {
                                this.aimoTokenStorageService.activateSession(token);
                                this.authRedirectService.aimoRedirect();
                            }
                        )
                        .catch(
                            () => this.eventService.dispatch(new LoginFail())
                        );
                }
            );
    }

    async loginWithCredentials(userId, password) {
        /*    let uid = userId;
            if (this.authMultisiteIsolationService) {
                uid = await lastValueFrom(this.authMultisiteIsolationService.decorateUserId(uid));
            }
            await this.oAuthLibWrapperService.authorizeWithPasswordFlow(uid, password).then(
                result => {
                    // OCC specific user id handling. Customize when implementing different backend
                    this.userIdService.setUserId(OCC_USER_ID_CURRENT);
                    this.store.dispatch(new AuthActions.Login());
                }
            );*/
    }

    async coreLogout(): Promise<void> {
        if (this.cdcLogoutUrl) {
            openCloseSpinner(this.document, true);
            return await super.coreLogout().then(() => this.cdcLogout());
        } else {
            return await super.coreLogout().finally(() => this.routingService.go('/login'));
        }
    }

    private handleCdcLogout(): void {
        this.baseSiteService.get()
            .subscribe((site: AimoBaseSite) => {
                if (site && this.cdcLogoutUrl === undefined) {
                    this.cdcLogoutUrl = site.cdcLogoutUrl;
                }
            });
        this.userService.get()
            .subscribe((user: AimoUser) => {
                if (user?.cdcLogoutUrl) {
                    this.cdcLogoutUrl = user.cdcLogoutUrl;
                }
            });

    }

    cdcLogout(): void {
        this.languageService.getActive()
            .pipe(take(1))
            .subscribe(lang => {
                    this.document.location.href = this.cdcLogoutUrl.replaceAll(LANGUAGE_PLACEHOLDER, lang ?? 'fi');
                }
            );
    }

    public isCDCLoginActivated(): Observable<boolean> {
        return this.baseSiteService.get().pipe(
            map((site: AimoBaseSite) =>
                site.loginUrl !== null &&
                site.loginUrl !== undefined
            )
        );
    }

    logout() {
        // no such page in aimo. this will break the CIAM logout
//        this.routingService.go({ cxRoute: 'logout' });
    }
}
